import { useCallback, useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { ConfirmDialog } from "./common";
import { Upload, Loader2, Trash2, ImageIcon, Search, Check } from "lucide-react";
import { toast } from "sonner";

const ORIGIN = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
export const mediaSrc = (path, thumb) => (!path ? "" : /^https?:|^data:/.test(path) ? path : `${ORIGIN}${path}${thumb ? "?thumb=1" : ""}`);

// Medya kütüphanesi paneli (sayfa olarak ya da seçici olarak kullanılır)
export function MediaLibrary({ onPick, compact = false }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [del, setDel] = useState(null);
  const [drag, setDrag] = useState(false);
  const input = useRef(null);

  const load = useCallback(() => api.get("/media").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const upload = async (files) => {
    setBusy(true);
    for (const f of files) {
      const fd = new FormData(); fd.append("file", f);
      try { await api.post("/media", fd); } catch (e) { toast.error(`${f.name}: ${e.response?.data?.detail || "yüklenemedi"}`); }
    }
    setBusy(false); load();
  };
  const shown = items.filter((i) => !q || i.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-3" onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); if (e.dataTransfer.files?.length) upload([...e.dataTransfer.files]); }}>
      <div className="flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Görsel ara" className="pl-9" />
        </div>
        <input ref={input} type="file" accept="image/*" multiple hidden onChange={(e) => { upload([...e.target.files]); e.target.value = ""; }} />
        <Button onClick={() => input.current?.click()} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="media-upload-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />} Görsel yükle
        </Button>
      </div>
      <div className={`grid ${compact ? "grid-cols-3 sm:grid-cols-4" : "grid-cols-2 sm:grid-cols-4 lg:grid-cols-6"} gap-3 ${drag ? "ring-2 ring-blue-400 rounded-lg" : ""}`} data-testid="media-grid">
        {shown.length === 0 && (
          <div className="col-span-full text-center py-12 text-slate-500 border-2 border-dashed border-slate-200 rounded-lg">
            <ImageIcon className="h-10 w-10 mx-auto text-slate-300 mb-2" />Görsel yok — sürükleyip bırakın ya da “Görsel yükle”ye tıklayın
          </div>
        )}
        {shown.map((m) => (
          <div key={m.id} className="group relative rounded-lg border border-slate-200 bg-white overflow-hidden">
            <button type="button" className="block w-full aspect-square bg-[repeating-conic-gradient(#f1f5f9_0%_25%,#fff_0%_50%)] bg-[length:16px_16px]" onClick={() => onPick && onPick(m)} data-testid={`media-item-${m.id}`}>
              <img src={mediaSrc(m.path, true)} alt={m.name} className="w-full h-full object-contain" loading="lazy" />
            </button>
            <div className="px-2 py-1.5 text-[11px] text-slate-600 truncate" title={m.name}>{m.name}</div>
            <div className="px-2 pb-1.5 text-[10px] text-slate-400">{m.w ? `${m.w}×${m.h} • ` : ""}{Math.round(m.size / 1024)} KB</div>
            {onPick && <div className="absolute inset-x-0 top-0 hidden group-hover:flex justify-center py-1 bg-blue-600/90 text-white text-xs font-semibold pointer-events-none"><Check className="h-3.5 w-3.5 mr-1" />Seç</div>}
            <button type="button" className="absolute top-1 right-1 h-6 w-6 rounded bg-white/90 text-rose-600 hidden group-hover:flex items-center justify-center" onClick={() => setDel(m)} title="Sil"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        ))}
      </div>
      <ConfirmDialog open={!!del} onOpenChange={(v) => !v && setDel(null)} title="Görsel silinsin mi?" description="Bu görseli kullanan sayfalarda görsel kaybolur." onConfirm={async () => { await api.delete(`/media/${del.id}`); setDel(null); load(); }} />
    </div>
  );
}

export default function MediaPicker({ open, onOpenChange, onPick }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto" data-testid="media-picker">
        <DialogHeader><DialogTitle>Görsel seç</DialogTitle><DialogDescription>Kütüphaneden seçin veya yeni görsel yükleyin (otomatik optimize edilir).</DialogDescription></DialogHeader>
        <MediaLibrary compact onPick={(m) => { onPick(m); onOpenChange(false); }} />
      </DialogContent>
    </Dialog>
  );
}
